import { BMDropdownOptionsValue } from "./types";

export type BMDropdownOptionRow<T> =
  | {
      type: "group";
      key: string;
      label: string;
      items: BMDropdownOptionsValue<T>[];
    }
  | {
      type: "item";
      key: string;
      item: BMDropdownOptionsValue<T>;
      indent: boolean;
    };

/**
 * Walks `allItems` (the full, unfiltered list) and groups adjacent items
 * sharing the same `group` value under one "group" row, followed by their
 * "item" rows (indent: true). Ungrouped items become standalone "item" rows.
 * A row is only included if at least one of its items survives `visibleItems`
 * (the search-filtered subset) — group membership/counts still reflect the
 * full group, only visibility is filtered.
 */
export function buildOptionRows<T>(
  allItems: BMDropdownOptionsValue<T>[],
  visibleItems: BMDropdownOptionsValue<T>[]
): BMDropdownOptionRow<T>[] {
  const visible = new Set(visibleItems);
  const rows: BMDropdownOptionRow<T>[] = [];

  let index = 0;
  while (index < allItems.length) {
    const item = allItems[index];

    if (!item.group) {
      if (visible.has(item)) {
        rows.push({ type: "item", key: `item-${index}`, item, indent: false });
      }
      index += 1;
      continue;
    }

    const group = item.group;
    const groupItems: BMDropdownOptionsValue<T>[] = [];
    let end = index;
    while (end < allItems.length && allItems[end].group === group) {
      groupItems.push(allItems[end]);
      end += 1;
    }

    const visibleGroupItems = groupItems.filter((it) => visible.has(it));
    if (visibleGroupItems.length > 0) {
      rows.push({ type: "group", key: `group-${index}`, label: group, items: groupItems });
      visibleGroupItems.forEach((it, i) => {
        rows.push({
          type: "item",
          key: `group-${index}-item-${i}`,
          item: it,
          indent: true,
        });
      });
    }

    index = end;
  }

  return rows;
}
